import { Search } from '@shared/components/ui/Search';
import { Sort, type SortCallbackData, type SortOption } from '@shared/components/ui/Sort';
import { useQueryParams } from '@shared/hooks/useQueryParams';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FiltersSectionProps {
  handleSearch: (value: string) => void;
  handleSort?: (data: SortCallbackData) => void;
  placeholder?: string;
  sortOptions?: SortOption[];
  defaultSortLabel?: string;
}

export const FiltersSection = ({
  handleSearch,
  handleSort,
  placeholder,
  sortOptions,
  defaultSortLabel,
}: FiltersSectionProps) => {
  // 🎯 Hook de traducciones
  const { t } = useTranslation('packsModule');

  // 🎯 Hook para manejar query parameters
  const { params, updateParam, updateParams } = useQueryParams({
    search: { type: 'string', default: '' },
    sortBy: { type: 'string', default: 'created_at' },
    sortDirection: { type: 'string', default: 'desc' },
  });

  // Estado local para el input (antes del submit)
  const [inputValue, setInputValue] = useState(params.search);

  // 🎯 Valores por defecto con traducciones
  const defaultPlaceholder = placeholder || t('filter.search_placeholder');
  const defaultSortOptions = sortOptions || [
    { field: 'title', direction: 'asc', label: t('table.title') + ' A-Z' },
    { field: 'title', direction: 'desc', label: t('table.title') + ' Z-A' },
    { field: 'created_at', direction: 'desc', label: t('filter.newest') },
    { field: 'created_at', direction: 'asc', label: t('filter.oldest') },
  ];
  const defaultSortLabelText = defaultSortLabel || t('filter.sort');

  // 🎯 Función para encontrar la opción de sort desde URL
  const findSortOptionFromURL = () => {
    const foundOption = defaultSortOptions.find(
      (option) => option.field === params.sortBy && option.direction === params.sortDirection,
    );
    return foundOption || defaultSortOptions[0];
  };

  // 🎯 Obtener valores iniciales de URL para el Sort
  const getInitialSortValue = () => {
    return {
      field: params.sortBy,
      direction: params.sortDirection as 'asc' | 'desc',
    };
  };

  // 🎯 Aplicar valores desde URL al montar o cuando cambien
  useEffect(() => {
    // Sincronizar input con URL solo si es diferente para evitar bucles
    if (params.search !== inputValue) {
      setInputValue(params.search);
    }
  }, [params.search]);

  // 🎯 Efecto separado para notificar al padre - solo al montar
  useEffect(() => {
    // Notificar al padre sobre los valores iniciales
    handleSearch(params.search);

    // 🎯 Aplicar sort inicial desde URL
    if (handleSort) {
      const initialSortOption = findSortOptionFromURL();
      handleSort({
        field: initialSortOption.field,
        direction: initialSortOption.direction,
        label: initialSortOption.label,
      });
    }
  }, [params.search, params.sortBy, params.sortDirection, handleSearch, handleSort]);

  // 🎯 Handler para el cambio del input - Solo afecta al estado local
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // 🎯 Handler para ejecutar búsqueda
  const handleSearchSubmit = useCallback(() => {
    const trimmedValue = inputValue.trim();
    console.log('🔍 Ejecutando búsqueda:', { inputValue, trimmedValue, currentParams: params.search });

    // Solo actualizar si el valor realmente cambió
    if (trimmedValue !== params.search) {
      updateParam('search', trimmedValue);
      handleSearch(trimmedValue);
    }
  }, [inputValue, updateParam, handleSearch, params.search]);

  // 🎯 Handler para el ordenamiento
  const handleSortChange = useCallback(
    (data: SortCallbackData) => {
      console.log('🔄 Ejecutando ordenamiento:', data, {
        currentSortBy: params.sortBy,
        currentDirection: params.sortDirection,
        newField: data.field,
        newDirection: data.direction,
      });

      // Verificar que los datos son válidos
      if (!data.field || !data.direction) {
        console.error('❌ Datos de ordenamiento inválidos:', data);
        return;
      }

      // 🎯 Actualizar parámetros de ordenamiento en URL - DE UNA SOLA VEZ
      try {
        console.log('🔄 Actualizando ambos parámetros juntos:', { sortBy: data.field, sortDirection: data.direction });

        updateParams({
          sortBy: data.field,
          sortDirection: data.direction,
        });

        console.log('✅ Parámetros URL actualizados correctamente');
      } catch (error) {
        console.error('❌ Error actualizando parámetros URL:', error);
      }

      // 🎯 Llamar al handler padre
      if (handleSort) {
        handleSort(data);
      }
    },
    [updateParams, handleSort, params.sortBy, params.sortDirection],
  );

  return (
    <div className="flex gap-3 items-center">
      <div className="flex-1">
        <Search
          placeholder={defaultPlaceholder}
          value={inputValue}
          onChange={handleInputChange}
          onEnter={handleSearchSubmit}
          dataTestId="packs-search"
          inputDataTestId="packs-search-input"
        />
      </div>

      {handleSort && (
        <Sort
          options={defaultSortOptions}
          onSort={handleSortChange}
          defaultLabel={defaultSortLabelText}
          initialValue={getInitialSortValue()}
          dataTestId="packs-sort"
        />
      )}
    </div>
  );
};
